<script setup lang="ts">
import { ref, computed } from "vue";
import { v4 as uuidv4 } from "uuid";

import { useTheme } from "@/features/Themes";

const props = withDefaults(
  defineProps<{
    name: string;
    value: string | number;
    checked: boolean;
    label?: string;
    disabled?: boolean;
    hover?: boolean;
    theme?: "appBlue" | "slate";
  }>(),
  {
    label: undefined,
    disabled: false,
    hover: false,
    theme: "appBlue",
  }
);

const emit = defineEmits<{
  change: [payload: { name: string; value: string | number }];
}>();

const radioButtonId = ref<string>(uuidv4());

function onChange(event: Event) {
  if (props.disabled) return;
  const target = event.target as HTMLInputElement;
  emit("change", { name: props.name, value: target.value });
}

const theme = computed(() =>
  useTheme().get({
    config: {
      appBlue: {
        passive: {
          bgLabel: props.hover ? "hover:bg-app-blue-50" : "",
          bg: "bg-app-blue-50",
          border: "border-app-blue-600",
          icon: "text-app-blue-600",
          bgChecked: "bg-app-blue-600",
          ring: "peer-focus-visible:ring-app-blue-600",
        },
      },
      slate: {
        passive: {
          bgLabel: props.hover ? "hover:bg-slate-50" : "",
          bg: "bg-slate-0",
          border: "border-slate-600",
          icon: "text-slate-600",
          bgChecked: "bg-slate-600",
          ring: "peer-focus-visible:ring-slate-600",
        },
      },
    },
    theme: props.theme,
  })
);
</script>

<template>
  <label
    :for="radioButtonId"
    class="t-small rounded-4 p-px-8 duration-fast ease-standard block bg-transparent transition-colors"
    :class="[theme.bgLabel, disabled ? 'cursor-default opacity-60' : 'cursor-pointer']">
    <input
      :id="radioButtonId"
      :name="name"
      type="radio"
      :value="value"
      class="peer sr-only"
      :checked="checked"
      :disabled="disabled"
      :aria-label="label || undefined"
      @click="onChange" />

    <div class="gap-px-8 flex items-center">
      <div
        aria-hidden="true"
        class="h-px-16 w-px-16 flex shrink-0 items-center justify-center rounded-full border peer-focus-visible:ring-2 peer-focus-visible:ring-offset-2"
        :class="[theme.bg, theme.border, theme.ring]">
        <span
          v-if="checked"
          class="h-px-8 w-px-8 rounded-full"
          :class="[theme.bgChecked]" />
      </div>

      <span
        v-if="label"
        class="text-type-default whitespace-nowrap">
        {{ label }}
      </span>
    </div>
  </label>
</template>
